import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { DefaultFormMoneyInput } from "../../../../../../common/components/default_form_money_input";
import { ItemVerbaDropDown } from "../../../../domain/models/item_verba_dropdown";
import { DefaultAutocomplete } from "../../../../../../common/components/default_autocomplete";
import { zodResolver } from "@hookform/resolvers/zod";
import { EventoForm, EventoSchema } from "../../../schemas/Funcionarios_cadastro/evento_schema";
import { useCallback, useEffect } from "react";
import { Evento } from "../../../../domain/models/evento";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * CadastroEventoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedEvento: Evento | null,
  itensVerbaDropDown: ItemVerbaDropDown[],
  onSubmit: (form: EventoForm) => Promise<void>,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onNovoItemVerba: () => void,
  onClose: () => void,
};

export const CadastroEventoModal = ({
  open,
  saving,
  selectedEvento,
  itensVerbaDropDown,
  onSubmit,
  onOpenChangeHistory,
  onNovoItemVerba,
  onClose,
}: Props) => {
  const hookForm = useForm<EventoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(EventoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedEvento) {
        hookForm.setValue('idItemVerba', selectedEvento.idItemVerba);
        hookForm.setValue('valor', selectedEvento.valorUnitario);
        hookForm.setValue('descricao', selectedEvento.descricao);
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedEvento,
  ]);

  const handleSubmit = useCallback((form: EventoForm) => {
    if (!saving) onSubmit(form);
  }, [
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [
    saving,
    onClose,
  ]);

  const handleChangeHistory = useCallback(() => {
    if (selectedEvento) {
      onOpenChangeHistory(selectedEvento.idItemVerbaFuncionario, LogTipoTabela.ITEM_VERBA_FUNCIONARIO);
    }
  }, [selectedEvento, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Evento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <Controller
                name="idItemVerba"
                control={hookForm.control}
                render={({ field }) => {
                  return (
                    <DefaultAutocomplete
                      label='Item Verba'
                      options={itensVerbaDropDown}
                      getOptionLabel={(opt: ItemVerbaDropDown) => opt.descricao}
                      getOptionKey={(opt: ItemVerbaDropDown) => opt.idItemVerba}
                      required={true}
                      readOnly={saving}
                      value={itensVerbaDropDown.find((x) => x.idItemVerba === field.value)}
                      onChange={(newValue) => {
                        if (newValue && newValue.valorItemVerba) {
                          hookForm.setValue('valor', newValue.valorItemVerba);
                        }
                        field.onChange(newValue?.idItemVerba);
                      }}
                    />
                  );
                }} />

              <DefaultFormMoneyInput
                name='valor'
                label='Valor'
                required={true}
                readOnly={saving}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='descricao'
                label='Descrição'
                multiline={true}
                readOnly={saving}
                rows={3}
                reactHookForm={hookForm}
              />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedEvento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}